import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, Navigation, ChevronRight, Calendar, Zap, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const HOUR_START = 9;
const HOUR_END = 19;

function getNextWorkingDays(count) {
  const days = [];
  let current = new Date();
  current.setDate(current.getDate() + 1); // commence à demain
  while (days.length < count) {
    const dow = current.getDay();
    if (dow !== 0 && dow !== 6) { // pas sam/dim
      days.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function formatDate(d) {
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatKm(km) {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${Math.round(km)}km`;
}

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h${m > 0 ? String(m).padStart(2, '0') : '00'}`;
  return `${m}min`;
}

const CreneauxOptimisesPage = () => {
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');
  const [workingDays, setWorkingDays] = useState([]);
  const [dayData, setDayData] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const addr = params.get('address') || '';
    const la = parseFloat(params.get('lat')) || 0;
    const ln = parseFloat(params.get('lng')) || 0;
    const cp = params.get('cp') || '';
    const vi = params.get('ville') || '';
    setAddress(addr);
    setLat(la);
    setLng(ln);
    setCodePostal(cp);
    setVille(vi);

    if (la && ln) {
      loadData(la, ln);
    } else if (addr) {
      geocodeAndLoad(addr);
    } else {
      setLoading(false);
    }
  }, []);

  const geocodeAndLoad = async (addr) => {
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addr)}&limit=1`);
      const data = await res.json();
      if (data.features?.length > 0) {
        const coords = data.features[0].geometry.coordinates;
        setLng(coords[0]);
        setLat(coords[1]);
        setCodePostal(data.features[0].properties.postcode || '');
        setVille(data.features[0].properties.city || '');
        await loadData(coords[1], coords[0]);
      } else {
        toast.error('Adresse non trouvée');
        setLoading(false);
      }
    } catch (e) {
      toast.error('Erreur géocodage');
      setLoading(false);
    }
  };

  const loadData = async (latitude, longitude) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const days = getNextWorkingDays(5);
      setWorkingDays(days);

      // Charger : RDV visibles (pour distances) + disponibilité RÉELLE du secteur + planning slots
      const dept = (codePostal || '').substring(0, 2);
      
      const [rdvRes, availRes, slotsRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/api/rendez-vous/calendar`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/rendez-vous/availability`, { params: { dept }, headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { slots: [] } })),
        axios.get(`${API}/api/planning-slots`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/app-config/planning_defaults`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      const myRdvs = rdvRes.data?.rdvs || rdvRes.data || [];
      const allSectorSlots = availRes.data?.slots || []; // TOUS les RDV du secteur (sans info prospect)
      const allSlots = slotsRes.data || [];
      const planningDefaults = defaultsRes.data || {};
      
      // Département du prospect
      const SECTORS = [
        { key: 'rhone_alpes', departements: ['38','01','42','73','71','74','39','25','21','63','03','43','07','26'] },
        { key: 'idf_alentours', departements: ['80','02','60','27','28','95','91','94','92','77','59','62','10','76','45','78'] },
      ];
      const prospectSector = SECTORS.find(s => s.departements.includes(dept));
      const sectorKey = prospectSector?.key || '';

      // Pour chaque jour, trouver les RDV et calculer les distances
      const results = [];
      for (const day of days) {
        const dayStr = day.toISOString().split('T')[0];
        
        // RDV visibles par l'utilisateur (pour afficher les distances)
        const dayRdvs = myRdvs.filter(r => {
          const rdvDate = r.date_rdv ? new Date(r.date_rdv) : null;
          return rdvDate && rdvDate.toISOString().split('T')[0] === dayStr && r.prospect;
        });
        
        // TOUS les RDV du secteur ce jour (pour calculer les créneaux occupés — même ceux des autres)
        const allSectorDayRdvs = allSectorSlots.filter(r => {
          const rdvDate = r.date_rdv ? new Date(r.date_rdv) : null;
          return rdvDate && rdvDate.toISOString().split('T')[0] === dayStr;
        });

        // Calculer distances via matrix API
        let rdvsWithDistance = [];
        if (dayRdvs.length > 0) {
          const locations = [[longitude, latitude]]; // Point de référence
          const validRdvs = [];
          
          for (const rdv of dayRdvs) {
            const p = rdv.prospect;
            const pLat = p?.latitude;
            const pLng = p?.longitude;
            if (pLat && pLng && pLat !== 0 && pLng !== 0) {
              locations.push([pLng, pLat]);
              validRdvs.push(rdv);
            } else if (p?.adresse) {
              // Geocoder à la volée
              try {
                const geoRes = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(p.adresse + ' ' + (p.code_postal || '') + ' ' + (p.ville || ''))}&limit=1`);
                const geoData = await geoRes.json();
                if (geoData.features?.length > 0) {
                  const c = geoData.features[0].geometry.coordinates;
                  locations.push(c);
                  validRdvs.push(rdv);
                }
              } catch (e) { /* skip */ }
            }
          }

          if (locations.length > 1) {
            try {
              const matrixRes = await axios.post(`${API}/api/routing/matrix`, {
                locations,
                metrics: ["distance", "duration"],
                sources: [0],
                destinations: Array.from({ length: locations.length - 1 }, (_, i) => i + 1)
              }, { headers: { Authorization: `Bearer ${token}` } });

              const distances = matrixRes.data.distances?.[0] || [];
              const durations = matrixRes.data.durations?.[0] || [];

              const isFallback = matrixRes.data.fallback === true;

              rdvsWithDistance = validRdvs.map((rdv, i) => ({
                ...rdv,
                distance_km: distances[i] != null ? (isFallback ? distances[i] : distances[i] / 1000) : null,
                duration_min: durations[i] ? Math.round(durations[i] / 60) : null
              }));
            } catch (e) {
              console.error('Matrix error:', e);
              rdvsWithDistance = validRdvs.map(r => ({ ...r, distance_km: null, duration_min: null }));
            }
          }
        }

        // Trier par distance (le plus proche en premier)
        rdvsWithDistance.sort((a, b) => (a.distance_km || 9999) - (b.distance_km || 9999));

        // Vérifier la disponibilité du planning pour ce jour/secteur
        let planningMax = 0;
        let planningUsed = allSectorDayRdvs.length; // Utilise TOUS les RDV du secteur
        if (sectorKey) {
          const slotForDay = allSlots.find(s => s.date === dayStr && s.secteur === sectorKey);
          planningMax = slotForDay ? slotForDay.max_rdv : (planningDefaults[sectorKey] || 0);
        }
        const planningRemaining = Math.max(0, planningMax - planningUsed);
        const planningFull = planningMax > 0 && planningRemaining <= 0;
        const noPlanningConfigured = planningMax === 0 && !!sectorKey;
        const planningInfo = { max: planningMax, used: planningUsed, remaining: planningRemaining, full: planningFull, noConfig: noPlanningConfigured, sector: sectorKey };

        // Trouver le meilleur créneau basé sur TOUS les RDV du secteur (pas juste les miens)
        const suggestedSlot = findBestSlot(allSectorDayRdvs, rdvsWithDistance, day, planningInfo);

        results.push({
          date: day,
          rdvs: rdvsWithDistance,
          allRdvs: dayRdvs,
          suggestedSlot: planningFull ? null : suggestedSlot,
          hasNearbyRdv: rdvsWithDistance.some(r => r.distance_km !== null && r.distance_km < 300),
          planning: { max: planningMax, used: planningUsed, remaining: planningRemaining, full: planningFull, noConfig: noPlanningConfigured, sector: sectorKey }
        });
      }

      // Trier les jours : celui avec le RDV le plus proche en premier
      results.sort((a, b) => {
        const minA = a.rdvs.length > 0 ? Math.min(...a.rdvs.map(r => r.distance_km || 9999)) : 9999;
        const minB = b.rdvs.length > 0 ? Math.min(...b.rdvs.map(r => r.distance_km || 9999)) : 9999;
        return minA - minB;
      });

      setDayData(results);
    } catch (e) {
      console.error('Error loading data:', e);
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  };

  // === LOGIQUE DE CRÉNEAUX — prend en compte les plannings ===
  // Un planning = 3-4 RDV séquentiels dans la journée. Chaque RDV bloque 1h30 (1h RDV + 30min trajet).
  // Les créneaux proposés doivent être ENTRE ou APRÈS les RDV existants, jamais en conflit.
  
  const computeFreeSlots = (allDayRdvs, planningInfo) => {
    // Créneaux possibles de 9h à 19h (inclus)
    const ALL_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
    
    // Calculer les plages horaires occupées (chaque RDV = 1h30)
    const busyRanges = [];
    for (const rdv of allDayRdvs) {
      const d = new Date(rdv.date_rdv);
      const startH = d.getHours() + d.getMinutes() / 60;
      const endH = startH + 1.5; // 1h30 par RDV
      busyRanges.push({ start: startH, end: endH });
    }
    
    // Trouver les heures libres (pas en conflit avec les plages occupées)
    const freeHours = ALL_HOURS.filter(h => {
      return !busyRanges.some(range => {
        // Le créneau [h, h+1.5] chevauche [range.start, range.end] ?
        return h < range.end && (h + 1.5) > range.start;
      });
    });
    
    // Si planning complet, aucun créneau
    if (planningInfo?.full) return [];
    
    return freeHours;
  };

  const findBestSlot = (allDayRdvs, rdvsWithDist, day, planningInfo) => {
    const freeHours = computeFreeSlots(allDayRdvs, planningInfo);
    if (freeHours.length === 0) return null;
    
    // Filtrer les RDV proches (< 300km)
    const nearbyRdvs = rdvsWithDist.filter(r => r.distance_km !== null && r.distance_km < 300);
    const nearestRdv = nearbyRdvs.length > 0 ? nearbyRdvs[0] : null;
    
    // Scorer chaque créneau libre
    let bestHour = freeHours[0];
    let bestScore = Infinity;
    
    for (const h of freeHours) {
      let score = 50;
      
      if (nearestRdv) {
        const nearH = new Date(nearestRdv.date_rdv).getHours();
        // Préférer un créneau adjacent au RDV le plus proche (trajet optimisé)
        const gap = Math.abs(h - nearH);
        score = gap * 10;
        if (gap <= 2) score -= 40; // Très proche = excellent
        if (nearestRdv.distance_km < 50) score -= 20; // Distance courte = bonus
      }
      
      // Léger bonus pour les créneaux classiques (9h, 10h, 14h, 15h)
      if ([9, 10, 14, 15].includes(h)) score -= 5;
      
      if (score < bestScore) {
        bestScore = score;
        bestHour = h;
      }
    }
    
    return {
      hour: bestHour,
      date: day,
      nearestRdv,
      freeHours,
      reason: nearestRdv 
        ? `Proche de ${nearestRdv.prospect?.nom || 'RDV'} (${formatKm(nearestRdv.distance_km || 0)}, ${formatDuration(nearestRdv.duration_min)})` 
        : 'Premier créneau disponible'
    };
  };

  const handleSelectSlot = (day, hour) => {
    const d = new Date(day);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(hour).padStart(2,'0')}:00`;
    // Ouvrir la page prospects avec le créneau pré-rempli + adresse
    const params = new URLSearchParams({
      create: 'true',
      date_rdv: dateStr,
      address: address,
      cp: codePostal,
      ville: ville
    });
    window.opener?.postMessage({ type: 'SELECT_CRENEAU', dateStr, address, codePostal, ville }, '*');
    window.location.href = `/prospects?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Calcul des distances et créneaux optimaux...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="outline" size="sm" onClick={() => window.close()}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="text-xl font-bold text-gray-900">Créneaux Optimisés</h1>
        </div>
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <MapPin className="w-5 h-5 text-blue-600" />
          <div>
            <p className="font-medium">{address}</p>
            <p className="text-sm text-gray-500">{codePostal} {ville} — Lat: {lat.toFixed(4)}, Lng: {lng.toFixed(4)}</p>
          </div>
        </div>
      </div>

      {/* 5 jours */}
      <div className="max-w-6xl mx-auto space-y-4">
        {dayData.map((day, i) => (
          <Card key={i} className="overflow-hidden">
            <div className="bg-white border-b p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${day.hasNearbyRdv ? 'bg-green-100' : day.rdvs.length > 0 ? 'bg-orange-100' : 'bg-blue-100'}`}>
                  <Calendar className={`w-5 h-5 ${day.hasNearbyRdv ? 'text-green-600' : day.rdvs.length > 0 ? 'text-orange-600' : 'text-blue-600'}`} />
                </div>
                <div>
                  <h2 className="font-semibold capitalize">{formatDate(day.date)}</h2>
                  <p className="text-sm text-gray-500">{day.allRdvs.length} RDV ce jour</p>
                </div>
              </div>
              
              {/* Info planning + Créneau suggéré */}
              <div className="flex items-center gap-2">
                {day.planning?.noConfig ? (
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-1 rounded">Pas de planning</span>
                ) : day.planning?.full ? (
                  <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded font-bold">COMPLET {day.planning.used}/{day.planning.max}</span>
                ) : day.planning?.max > 0 ? (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">{day.planning.remaining} place(s) restante(s)</span>
                ) : null}
                
                {day.suggestedSlot && !day.planning?.full && (
                  <Button size="sm"
                    onClick={() => handleSelectSlot(day.date, day.suggestedSlot.hour)}
                    className="bg-green-600 hover:bg-green-700 text-xs h-7"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Suggéré : {day.suggestedSlot.hour}h00
                  </Button>
                )}
              </div>
            </div>
            
            <CardContent className="p-4">
              {day.planning?.full ? (
                <div className="text-center py-6">
                  <p className="text-red-500 font-bold text-sm">🚫 Planning complet pour ce jour</p>
                  <p className="text-xs text-gray-400 mt-1">{day.planning.used}/{day.planning.max} RDV déjà planifiés dans ce secteur</p>
                </div>
              ) : day.rdvs.filter(r => r.distance_km === null || r.distance_km < 300).length > 0 ? (
                <div className="space-y-2">
                  {day.rdvs.filter(r => r.distance_km === null || r.distance_km < 300).map((rdv, j) => {
                    const rdvDate = new Date(rdv.date_rdv);
                    const prospect = rdv.prospect;
                    const tooFar = rdv.duration_min > 90;
                    const medium = rdv.duration_min > 60 && rdv.duration_min <= 90;
                    return (
                      <div key={j} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        {/* Distance + Durée */}
                        <div className={`shrink-0 w-24 text-center p-2 rounded-lg ${
                          tooFar ? 'bg-red-100 text-red-700' :
                          medium ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          <p className="text-xl font-bold">{rdv.distance_km !== null ? formatKm(rdv.distance_km) : '?'}</p>
                          <p className="text-sm font-semibold">{rdv.duration_min > 0 ? formatDuration(rdv.duration_min) : ''}</p>
                          {tooFar && <p className="text-[10px] font-bold">TROP LOIN</p>}
                        </div>

                        {/* Heure */}
                        <div className="shrink-0 text-center w-16">
                          <p className="text-sm font-bold">{rdvDate.getHours()}h{String(rdvDate.getMinutes()).padStart(2,'0')}</p>
                          <p className="text-[10px] text-gray-400">{rdv.duree_minutes || 60}min</p>
                        </div>

                        {/* Infos prospect */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {prospect?.nom} {prospect?.prenom}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {prospect?.code_postal} {prospect?.ville} — {prospect?.adresse}
                          </p>
                        </div>

                        {/* Status */}
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {rdv.status}
                        </Badge>
                      </div>
                    );
                  })}
                  
                  {/* Tous les créneaux disponibles — calculés avec la logique planning */}
                  {day.suggestedSlot?.freeHours?.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Créneaux disponibles ({day.suggestedSlot.freeHours.length}) :</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {day.suggestedSlot.freeHours.map(h => (
                          <Button key={h} size="sm" variant="outline" onClick={() => handleSelectSlot(day.date, h)}
                            className={`text-xs h-7 ${day.suggestedSlot?.hour === h ? 'bg-green-100 border-green-400 text-green-700 font-bold ring-2 ring-green-300' : ''}`}>
                            {h}h00
                          </Button>
                        ))}
                      </div>
                      {day.suggestedSlot.reason && (
                        <p className="text-[10px] text-green-600 mt-1">💡 Suggestion {day.suggestedSlot.hour}h00 — {day.suggestedSlot.reason}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-4">
                  <p className="text-center text-sm text-green-600 font-medium mb-3">Journée complètement libre</p>
                  <div className="flex gap-1.5 flex-wrap justify-center">
                    {[9, 10, 11, 12, 14, 15, 16, 17, 18, 19].map(h => (
                      <Button key={h} size="sm" variant="outline" onClick={() => handleSelectSlot(day.date, h)}
                        className="text-xs h-7">
                        {h}h00
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CreneauxOptimisesPage;
